import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Briefcase, Mic, Users, Mail, MapPin, Linkedin } from 'lucide-react';
import { Section, SectionTitle } from '@/components/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formConfigs = {
  general: {
    title: 'General Inquiry',
    description: 'Questions, introductions, or general communication.',
    icon: Mail,
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'message', label: 'Message', type: 'textarea', required: true, placeholder: 'How can I help?' }
    ],
    endpoint: '/inquiries/general'
  },
  consulting: {
    title: 'Consulting Request',
    description: 'Technology strategy, AI implementation, or business advisory.',
    icon: Briefcase,
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'organization', label: 'Organization', type: 'text', required: false },
      { name: 'project_description', label: 'Project Description', type: 'textarea', required: true, placeholder: 'Describe your project or challenge...' },
      { name: 'budget_range', label: 'Budget Range', type: 'select', required: false, options: ['Under $10,000', '$10,000 - $50,000', '$50,000 - $100,000', '$100,000+', 'Not sure yet'] },
      { name: 'message', label: 'Additional Details', type: 'textarea', required: false }
    ],
    endpoint: '/inquiries/consulting'
  },
  speaking: {
    title: 'Speaking Request',
    description: 'Keynotes, workshops, panels, and conference presentations.',
    icon: Mic,
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'event_name', label: 'Event Name', type: 'text', required: true },
      { name: 'event_location', label: 'Event Location', type: 'text', required: false },
      { name: 'event_date', label: 'Event Date', type: 'text', required: false, placeholder: 'e.g., March 2025' },
      { name: 'topic_interest', label: 'Topic of Interest', type: 'text', required: false, placeholder: 'e.g., AI Strategy, Entrepreneurship' },
      { name: 'message', label: 'Event Details', type: 'textarea', required: true, placeholder: 'Tell me about your event and audience...' }
    ],
    endpoint: '/inquiries/speaking'
  },
  entrepreneur: {
    title: 'Entrepreneur Support',
    description: 'Mentorship, guidance, and collaboration for founders.',
    icon: Users,
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'business_type', label: 'Business Type', type: 'text', required: true, placeholder: 'e.g., SaaS, Publishing, AI Startup' },
      { name: 'business_stage', label: 'Business Stage', type: 'select', required: false, options: ['Idea Stage', 'Early Development', 'Launched', 'Growing', 'Scaling'] },
      { name: 'support_needed', label: 'Support Needed', type: 'textarea', required: true, placeholder: 'What kind of support are you looking for?' },
      { name: 'message', label: 'Additional Context', type: 'textarea', required: false }
    ],
    endpoint: '/inquiries/entrepreneur'
  }
};

function ContactForm({ config }) {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}${config.endpoint}`, formData);
      setIsSuccess(true);
      toast.success('Your inquiry has been submitted successfully!');
      setFormData({});
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">
          Thank you for reaching out. I'll review your message and respond soon.
        </p>
        <Button onClick={() => setIsSuccess(false)} variant="outline" className="border-primary text-primary hover:bg-primary/10">
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid={`form-${config.endpoint.split('/').pop()}`}>
      {config.fields.map((field) => (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name} className="text-slate-300">
            {field.label} {field.required && <span className="text-primary">*</span>}
          </Label>
          
          {field.type === 'textarea' ? (
            <Textarea
              id={field.name}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className="bg-input border-border focus:border-primary min-h-[120px]"
              data-testid={`input-${field.name}`}
            />
          ) : field.type === 'select' ? (
            <select
              id={field.name}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={field.required}
              className="w-full h-12 rounded-lg bg-input border border-border px-4 text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid={`select-${field.name}`}
            >
              <option value="">Select...</option>
              {field.options.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <Input
              id={field.name}
              type={field.type}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className="bg-input border-border focus:border-primary h-12"
              data-testid={`input-${field.name}`}
            />
          )}
        </div>
      ))}

      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
        data-testid="submit-btn"
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <motion.div
              className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            Sending...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Send Message
          </span>
        )}
      </Button>
    </form>
  );
}

export default function ContactPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl">
          <motion.span
            className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Contact
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="contact-headline"
          >
            Let's Connect
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Whether you're exploring consulting opportunities, seeking a speaker, or interested in collaboration, I welcome thoughtful conversations.
          </motion.p>
        </div>
      </Section>

      {/* Contact Forms */}
      <Section dark>
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="w-full grid grid-cols-2 md:grid-cols-4 h-auto gap-2 bg-transparent p-0 mb-8">
                {Object.entries(formConfigs).map(([key, config]) => (
                  <TabsTrigger 
                    key={key} 
                    value={key}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground bg-card border border-border py-3 flex items-center gap-2"
                    data-testid={`tab-${key}`}
                  >
                    <config.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{config.title.split(' ')[0]}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(formConfigs).map(([key, config]) => (
                <TabsContent key={key} value={key}>
                  <motion.div
                    className="bg-card border border-border rounded-xl p-6 md:p-8"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <config.icon className="w-6 h-6 text-primary" />
                      <h2 className="text-2xl font-bold text-white">{config.title}</h2>
                    </div>
                    <p className="text-muted-foreground mb-8">{config.description}</p>
                    <ContactForm config={config} />
                  </motion.div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Contact Info Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 sticky top-24">
              <h3 className="text-xl font-bold text-white mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Location</h4>
                    <p className="text-muted-foreground">Colorado Springs, Colorado</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Email</h4>
                    <a href="mailto:robert@rdbrunet.com" className="text-primary hover:underline">
                      robert@rdbrunet.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Linkedin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">LinkedIn</h4>
                    <a href="https://www.linkedin.com/in/robert-brunet-959713320" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-t border-border mt-8 pt-8">
                <h4 className="text-white font-medium mb-3">Response Time</h4>
                <p className="text-muted-foreground text-sm">
                  I typically respond to inquiries within 1-2 business days. For urgent matters, please indicate so in your message.
                </p>
              </div>

              <div className="border-t border-border mt-8 pt-8">
                <h4 className="text-white font-medium mb-4">Quick Connect</h4>
                <div className="bg-white p-3 rounded-lg inline-block">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/j8pebfxk_R_Dale_Brunet_Blinq_Card_QR_Code.jpg"
                    alt="R. Dale Brunet Contact QR Code"
                    className="w-28 h-28"
                  />
                </div>
                <p className="text-muted-foreground text-xs mt-2">
                  Scan to save contact info
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>
  );
}
